import React from "react";
import { Link } from "react-router-dom";
import { formatDate } from "@/utils/timezone";
import StockTwitsCard from "./StockTwitsCard";

/**
 * IntelCard - Reusable card component for displaying intel items.
 *
 * Props:
 * - intel: The intel item object
 * - onScreenshotClick: Optional callback when screenshot is clicked (for image viewer)
 * - onImageUrlClick: Optional callback when an image URL is clicked (for StockTwits charts)
 * - onTickerClick: Optional callback when ticker badge is clicked
 * - onAuthorClick: Optional callback when author is clicked
 * - onDeleteClick: Optional callback for delete button
 * - isDeleting: Boolean to show deleting state
 * - accountId: Optional account ID for ticker links
 * - showTickers: Whether to show ticker badges (default true, set false on ticker detail page)
 * - showDeleteButton: Whether to show delete button (default true)
 */
const SENTIMENT_DOT = {
  bullish: "#48bb78",
  bearish: "#fc8181",
  neutral: "#a0aec0",
};

const ACTION_LABEL = {
  buy_now: "buy now",
  wait_for_dip: "wait for dip",
  sell: "sell",
  hold: "hold",
  watching: "watching",
};

const IntelCard = ({
  intel,
  onScreenshotClick,
  onImageUrlClick,
  onTickerClick,
  onAuthorClick,
  onDeleteClick,
  onDetailClick,
  isDeleting = false,
  accountId,
  showTickers = true,
  showDeleteButton = true,
}) => {
  const isStockTwits = intel.platform === "stocktwits" && intel.raw_data;

  return (
    <div
      style={{
        background: "var(--card-bg)",
        border: "1px solid #e2e8f0",
        borderRadius: "8px",
        overflow: "hidden",
        transition: "all 0.2s",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Top right button - Link for StockTwits, Delete for others */}
      {isStockTwits && intel.url ? (
        <a
          href={intel.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: "0.5rem",
            right: "0.5rem",
            background: "rgba(59, 130, 246, 0.1)",
            border: "1px solid #3b82f6",
            borderRadius: "4px",
            color: "#3b82f6",
            fontSize: "1rem",
            padding: "0.375rem 0.5rem",
            cursor: "pointer",
            zIndex: 10,
            transition: "all 0.2s",
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#3b82f6";
            e.currentTarget.style.color = "white";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(59, 130, 246, 0.1)";
            e.currentTarget.style.color = "#3b82f6";
          }}
          title="View on StockTwits"
        >
          ↗
        </a>
      ) : showDeleteButton && onDeleteClick ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDeleteClick(intel.id, e);
          }}
          disabled={isDeleting}
          style={{
            position: "absolute",
            top: "0.5rem",
            right: "0.5rem",
            background: isDeleting ? "#cbd5e0" : "rgba(254, 215, 215, 0.95)",
            border: "1px solid #fc8181",
            borderRadius: "4px",
            color: "#c53030",
            fontSize: "0.75rem",
            padding: "0.25rem 0.5rem",
            cursor: isDeleting ? "not-allowed" : "pointer",
            fontWeight: "600",
            zIndex: 10,
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            if (!isDeleting) {
              e.currentTarget.style.background = "rgba(252, 129, 129, 0.95)";
              e.currentTarget.style.color = "white";
            }
          }}
          onMouseLeave={(e) => {
            if (!isDeleting) {
              e.currentTarget.style.background = "rgba(254, 215, 215, 0.95)";
              e.currentTarget.style.color = "#c53030";
            }
          }}
          title="Delete this intel"
        >
          {isDeleting ? "..." : "×"}
        </button>
      ) : null}

      {/* Pipeline status footer strip — always shown, clickable to open detail */}
      {onDetailClick && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDetailClick(intel);
          }}
          style={{
            position: "absolute",
            bottom: "0.5rem",
            right: "0.5rem",
            background: "rgba(255,255,255,0.92)",
            border: "1px solid #e2e8f0",
            borderRadius: "4px",
            padding: "0.2rem 0.45rem",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.3rem",
            zIndex: 10,
            transition: "all 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#ebf4ff";
            e.currentTarget.style.borderColor = "#90cdf4";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.92)";
            e.currentTarget.style.borderColor = "#e2e8f0";
          }}
          title="View pipeline detail"
        >
          {intel.analysis_summary ? (
            <>
              <span
                style={{
                  width: "7px",
                  height: "7px",
                  borderRadius: "50%",
                  background: SENTIMENT_DOT[intel.analysis_summary.sentiment] || "#a0aec0",
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: "0.65rem", color: "#4a5568", fontWeight: "500" }}>
                {ACTION_LABEL[intel.analysis_summary.action_type] || intel.analysis_summary.action_type}
              </span>
            </>
          ) : (
            <span style={{ fontSize: "0.65rem", color: "#a0aec0" }}>no analysis</span>
          )}
          <span style={{ fontSize: "0.65rem", color: "#90cdf4", marginLeft: "0.1rem" }}>⋯</span>
        </button>
      )}

      {/* Render StockTwits card or default card */}
      {isStockTwits ? (
        <div
          style={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <StockTwitsCard
            intel={intel}
            onTickerClick={onTickerClick}
            onAuthorClick={onAuthorClick}
            onImageClick={onImageUrlClick}
          />
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Screenshot */}
          {intel.screenshot_data && (
            <div
              style={{
                width: "100%",
                height: "200px",
                flexShrink: 0,
                overflow: "hidden",
                background: "#f7fafc",
                cursor: onScreenshotClick ? "pointer" : "default",
              }}
              onClick={(e) => {
                if (onScreenshotClick) {
                  e.stopPropagation();
                  onScreenshotClick(intel);
                }
              }}
              title={onScreenshotClick ? "Click to view full size" : undefined}
            >
              <img
                src={intel.screenshot_data}
                alt="Intel screenshot"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </div>
          )}

          {/* Content */}
          <div style={{ padding: "1rem", flex: 1, overflow: "auto" }}>
            {/* Metadata */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "0.75rem",
                flexWrap: "wrap",
              }}
            >
              {showTickers &&
                intel.tickers &&
                intel.tickers.map((ticker) => (
                  <Link
                    key={ticker}
                    to={`/ticker/${ticker}${accountId ? `?accountId=${accountId}` : ""}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      background: "#4299e1",
                      color: "white",
                      padding: "0.125rem 0.5rem",
                      borderRadius: "4px",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "background 0.2s",
                      textDecoration: "none",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#3182ce";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#4299e1";
                    }}
                    title={`View ${ticker} details`}
                  >
                    {ticker}
                  </Link>
                ))}
              {intel.platform && (
                <span style={{ fontSize: "0.75rem", color: "#718096" }}>
                  {intel.platform}
                </span>
              )}
            </div>

            {/* Author */}
            {intel.author && (
              <div
                style={{
                  fontSize: "0.875rem",
                  color: "#4a5568",
                  fontWeight: "600",
                  marginBottom: "0.5rem",
                  cursor: onAuthorClick ? "pointer" : "default",
                }}
                onClick={() => onAuthorClick && onAuthorClick(intel.author)}
              >
                {intel.author}
              </div>
            )}

            {/* Thesis */}
            {intel.thesis && (
              <div
                style={{
                  fontSize: "0.875rem",
                  color: "#2d3748",
                  marginBottom: "0.75rem",
                  lineHeight: "1.5",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {intel.thesis}
              </div>
            )}

            {/* Tags */}
            {intel.tags && intel.tags.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: "0.25rem",
                  flexWrap: "wrap",
                  marginBottom: "0.75rem",
                }}
              >
                {intel.tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      background: "#e2e8f0",
                      color: "#4a5568",
                      padding: "0.125rem 0.375rem",
                      borderRadius: "4px",
                      fontSize: "0.625rem",
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Timestamp */}
            <div style={{ fontSize: "0.7rem", color: "#a0aec0" }}>
              {formatDate(intel.timestamp, "MMM d, yyyy h:mm a")}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntelCard;
